<template>
  <div class="create-project-form">
    <form @submit.prevent="handleSubmit">
      <!-- Name -->
      <div class="form-group">
        <label for="name">Name:</label>
        <b-form-input
          id="name"
          v-model="project.name"
          placeholder="Enter Project name"
          required
          maxlength="20"
        />
        <small :class="['char-count', { 'at-limit': project.name.length >= 20 }]">
          {{ project.name.length }}/20 characters
          <span v-if="project.name.length >= 20"> - Maximum length reached</span>
        </small>
      </div>

      <!-- Description -->
      <div class="form-group">
        <label for="description">Description:</label>
        <b-form-textarea
          id="description"
          v-model="project.description"
          placeholder="Enter Project description"
          required
          rows="3"
        />
      </div>

      <!-- Type and Subtype Row -->
      <div class="d-flex justify-content-space-between">
        <div class="form-group w-100 me-2">
          <label for="type">Type:</label>
          <b-form-select id="type" v-model="project.type" required>
            <option disabled value="">Select Type</option>
            <option
              v-for="option in projectTypes"
              :key="option"
              :value="option.toLowerCase()"
            >
              {{ formatProjectTypeName(option) }}
            </option>
          </b-form-select>
        </div>

        <div class="form-group w-100">
          <label for="image_subtype">Subtype:</label>
          <b-form-select id="image_subtype" v-model="project.image_subtype">
            <option disabled value="">Select Image Subtype</option>
            <option value="x-ray">X-Ray</option>
            <option value="ct">CT</option>
            <option value="mri">MRI</option>
            <option value="ultrasound">Ultrasound</option>
            <option value="mammography">Mammography</option>
            <option value="other">Other</option>
          </b-form-select>
        </div>
      </div>

      <!-- Image Type and Grouping Row -->
      <div class="d-flex justify-content-space-between">
        <div class="form-group w-100 me-2">
          <label for="imageType">Image Type:</label>
          <b-form-select id="imageType" v-model="project.image_type">
            <option disabled value="">Select Image Type</option>
            <option
              value="single"
              :disabled="isCTOrMRI"
            >
              Single Image
            </option>
            <option value="sets">Image Sets</option>
          </b-form-select>
        </div>

        <div class="form-group w-100">
          <label for="imageGrouping">Image Grouping:</label>
          <b-form-select id="imageGrouping" v-model="project.image_grouping">
            <option disabled value="">Select Image Grouping</option>
            <option
              value="patient"
              :disabled="project.image_type !== 'sets'"
            >
              Patient
            </option>
            <option
              value="study"
              :disabled="project.image_type !== 'sets'"
            >
              Study
            </option>
            <option
              value="series"
              :disabled="project.image_type !== 'sets'"
            >
              Series
            </option>
            <option value="sets">Image (Instance)</option>
          </b-form-select>
        </div>
      </div>

      <!-- DICOM and Version Row -->
      <div class="d-flex justify-content-space-between">
        <div class="form-group w-100">
          <label>Enable DICOM Server:</label>
          <b-row class="mt-3 mb-2">
            <b-col class="text-center my-auto">
              <b-form-radio v-model="project.dicom" :value="true">
                Yes
              </b-form-radio>
            </b-col>
            <b-col class="text-center">
              <b-form-radio v-model="project.dicom" :value="false">
                No
              </b-form-radio>
            </b-col>
          </b-row>
        </div>

        <div class="form-group w-100 me-2">
          <label>RATIFY Labeller Version:</label>
          <b-row class="mt-3 mb-2">
            <b-col class="text-center my-auto">
              <b-form-radio
                v-model="project.version"
                :value="1"
                :disabled="isCTOrMRI"
              >
                v1
              </b-form-radio>
            </b-col>
            <b-col class="text-center">
              <b-form-radio v-model="project.version" :value="2">
                v1.2
              </b-form-radio>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Training Checkbox -->
      <b-form-checkbox
        id="enable_training"
        v-model="project.enable_training"
        :disabled="!allowTraining"
        class="ms-1 mb-3"
      >
        <p class="pt-1">Allow AI model training</p>
      </b-form-checkbox>

      <!-- Error Alert -->
      <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = ''">
        {{ error }}
      </b-alert>

      <!-- Submit Button -->
      <div class="d-flex justify-content-center">
        <b-button
          type="submit"
          :disabled="isLoading"
          pill
          size="sm"
          class="mb-4 px-5 create-button"
        >
          <font-awesome-icon icon="plus" class="create-icon" />
          <span v-if="isLoading">
            <i class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></i>
          </span>
          <span v-else>Create Project</span>
        </b-button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BFormSelect,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormRadio,
  BButton,
  BAlert,
  BRow,
  BCol
} from 'bootstrap-vue-next'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const store = useStore()
const emit = defineEmits(['close', 'created'])

// Form data
const project = ref({
  name: '',
  description: '',
  type: '',
  source: 'upload',
  members: [],
  enable_training: false,
  image_type: '',
  image_subtype: '',
  image_grouping: '',
  dicom: false,
  version: 2
})

const isLoading = ref(false)
const error = ref('')

// Computed properties
const auth = computed(() => store.state.auth)
const licenseInfo = computed(() => store.state.license?.info || {})

const projectTypes = computed(() => {
  const types = licenseInfo.value.projectTypes || []
  // Filter out light options
  return types.filter(type => !type.toLowerCase().includes('light'))
})

const allowTraining = computed(() => {
  return licenseInfo.value.training || false
})

const isCTOrMRI = computed(() => {
  return ['ct', 'mri'].includes(project.value.image_subtype)
})

// Watchers
watch(isCTOrMRI, (newValue) => {
  if (newValue) {
    // Auto-select v1.2 for CT/MRI
    project.value.version = 2
    // Auto-select sets for CT/MRI
    if (project.value.image_type === 'single') {
      project.value.image_type = 'sets'
    }
  }
})

watch(() => project.value.image_type, (newValue) => {
  // Reset image grouping when image type changes
  if (newValue !== 'sets') {
    project.value.image_grouping = ''
  }
})

// Methods
function formatProjectTypeName(type) {
  const typeMap = {
    'Retrospective': 'AI Evaluation',
    'Prospective': 'Monitoring',
    // 'Retro-Light': 'AI Evaluation (LLM)',
    // 'Pros-Light': 'Monitoring (Binary Light)'
  }
  return typeMap[type] || type
}

async function handleSubmit() {
  isLoading.value = true
  error.value = ''

  try {
    // Add current user to members
    const projectData = {
      ...project.value,
      members: [auth.value.user.id]
    }

    // Create project via Vuex
    const newProject = await store.dispatch('projectSubmission/createProject', projectData)

    // Reset form
    project.value = {
      name: '',
      description: '',
      type: '',
      source: 'upload',
      members: [],
      enable_training: false,
      image_type: '',
      image_subtype: '',
      image_grouping: '',
      dicom: false,
      version: 2
    }

    // Emit success events
    emit('created', newProject)
    emit('close')
  } catch (err) {
    console.error('Error creating project:', err)
    error.value = err.message || 'An error occurred while creating the Project'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.char-count {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--bs-secondary);
}

.char-count.at-limit {
  color: var(--bs-warning);
  font-weight: 500;
}

/* CreateProjectForm (migrated from global styles.css) */
.create-project-form {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 0.5rem 3rem;
}

.create-project-form .form-group {
  margin-bottom: 1rem;
}

.create-project-form label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--color-text-primary, #212529);
}

.create-button {
  background-color: #f0b93b !important;
  color: #14304c !important;
  border: #f0b93b !important;
  transition: all 0.2s ease-in-out;
}

.create-button:hover:not(:disabled) {
  background-color: #14304c !important;
  color: aliceblue !important;
  border-color: #14304c !important;
}

.create-icon {
  color: inherit;
  margin-right: 0.5rem;
}
</style>
